import {useState} from "react";
import {BackendError} from "@dvrt/graphql-widgets";
import {useGlobalNumberSearchQuery, GlobalNumberSearchQuery} from "./queries.generated";
import {Button, Table, TableBody, TableCell, TableRow, TextField} from "@mui/material";
import {FormattedMessage, defineMessage, defineMessages} from "react-intl";
import {useTranslations} from "../context/TranslationProvider";
import FormattedTableHead from "./FormattedTableHead";
import {Link} from "./widgets";

const typeLinkMap = {
    extension: "users",
    group: "call-groups",
    gw: "gateways",
    fax: "faxes",
    ivr: "ivrs",
    group_internal: "pickup-groups",
    call_queue: "call-queues",
};

const NumberLink = ({foundNumber}: {foundNumber: GlobalNumberSearchQuery["searchNumbers"]["numbers"][number]}) => {
    const url = `/domains/${foundNumber.domain.id}/${
        typeLinkMap[foundNumber.endpoint.type.toLowerCase() as keyof typeof typeLinkMap]
    }/${foundNumber.endpoint.id}`;
    return <Link to={url}>{foundNumber.shortNumber}</Link>;
};

type EndpointTypeKey = keyof typeof typeLinkMap; // extension | group | gw | fax | ivr | group_internal | call_queue

const ENDPOINT_TYPE_MESSAGES = defineMessages<EndpointTypeKey>({
    extension: {id: "globalSearchNumbers.types.extension", defaultMessage: "User"},
    fax: {id: "globalSearchNumbers.types.fax", defaultMessage: "Fax"},
    group: {id: "globalSearchNumbers.types.group", defaultMessage: "Call group"},
    group_internal: {id: "globalSearchNumbers.types.group_internal", defaultMessage: "Pickup group"},
    gw: {id: "globalSearchNumbers.types.gw", defaultMessage: "Gateway"},
    ivr: {id: "globalSearchNumbers.types.ivr", defaultMessage: "Auto Attendant"},
    call_queue: {id: "globalSearchNumbers.types.call_queue", defaultMessage: "Call queue"},
});

export default () => {
    const [filterString, setFilterString] = useState("");
    const [queryFilterString, setQueryFilterString] = useState("");

    const {error, data} = useGlobalNumberSearchQuery({
        variables: {
            filterString: queryFilterString,
        },
        fetchPolicy: "no-cache",
    });

    if (error) return <BackendError error={error} />;

    const numbers = data?.searchNumbers.numbers || [];
    const hasMore = data?.searchNumbers.hasMore || false;

    const translations = useTranslations();

    const HitCountText = () => {
        if (queryFilterString.length === 0) return <span>{"\u00A0"}</span>;

        if (hasMore)
            return (
                <FormattedMessage
                    id="globalSearchNumbers.hitCounterOverflow"
                    defaultMessage="{count, number}+ numbers have been found, showing first {count} results"
                    values={{count: numbers.length}}
                />
            );

        return (
            <FormattedMessage
                id="globalSearchNumbers.hitCounter"
                defaultMessage="{count, number} {count, plural, one {number has} other {numbers have} } been found"
                values={{count: numbers.length}}
            />
        );
    };

    const search = () => {
        setQueryFilterString(filterString);
    };

    return (
        <div>
            <div style={{display: "flex", alignItems: "baseline"}}>
                <TextField
                    variant="standard"
                    value={filterString}
                    onChange={(ev) => setFilterString(ev.target.value)}
                    placeholder={translations.get("globalSearchNumbers.placeholder")}
                    fullWidth
                    autoFocus
                    helperText={<HitCountText />}
                    onKeyUp={(ev) => {
                        if (ev.key === "Enter") search();
                        else if (ev.key === "Escape") {
                            setFilterString("");
                            setQueryFilterString("");
                        }
                    }}
                />
                <Button variant="contained" color="primary" style={{marginLeft: 10}} onClick={search}>
                    <FormattedMessage id="search" defaultMessage="Search" />
                </Button>
            </div>
            <Table>
                <FormattedTableHead
                    cells={[
                        {label: defineMessage({id: "shortNumber", defaultMessage: "Short number"})},
                        {label: defineMessage({id: "e164", defaultMessage: "E.164"})},
                        {label: defineMessage({id: "numberType.label", defaultMessage: "Number type"})},
                        {label: defineMessage({id: "mac_address", defaultMessage: "MAC address"})},
                        {label: defineMessage({id: "domain", defaultMessage: "Domain"})},
                    ]}
                />
                <TableBody style={{opacity: queryFilterString === filterString ? 1 : 0.5}}>
                    {numbers.map((foundNumber) => (
                        <TableRow key={`${foundNumber.shortNumber}${foundNumber.domain.id}`}>
                            <TableCell>
                                <NumberLink foundNumber={foundNumber} />
                            </TableCell>
                            <TableCell>{foundNumber.e164}</TableCell>
                            <TableCell>
                                <FormattedMessage
                                    {...ENDPOINT_TYPE_MESSAGES[
                                        foundNumber.endpoint.type.toLowerCase() as EndpointTypeKey
                                    ]}
                                />
                            </TableCell>
                            <TableCell>{foundNumber.macAddress}</TableCell>
                            <TableCell>
                                <Link to={`/domains/${foundNumber.domain.id}`}>{foundNumber.domain.name}</Link>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
};
